import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { Link } from 'react-router-dom';

import { Button, UncontrolledTooltip } from 'reactstrap';

import NotificationsItem from '../Notifications/Item';
import NothingFound from '../NothingFound';
import WidgetSkeletonNotification from './WidgetSkeletonNotification';

import { markAllNotificationsAsRead } from '../../actions/Notifications';

import { getUserSetting } from '../../helpers/Settings';

const DashboardWidgetNotifications = ({
  notifications,
  notificationsLoading,
  handleMarkAllAsRead,
}) => {
  const intl = useIntl();

  const [activeTab, setActiveTab] = useState('all');
  const [totalHeight, setTotalHeight] = useState(0);
  const [titleHeight, setTitleHeight] = useState(0);

  const titleRef = useRef(null);

  const userOrder = getUserSetting('dashboard_widgets') || [];
  const widget = userOrder.find(c => c.slug === 'notifications');
  const sameRowWidgets = userOrder
    .filter(c => (c.slug !== 'notifications' && c.y >= widget.y && c.y <= (widget.y + widget.h - 1)))
    .map(c => c.slug);

  const filteredNotifications = notifications.filter((n) => {
    if (activeTab === 'comments') {
      return n.type === 'CommentPosted';
    }

    return true;
  });

  useEffect(() => {
    if (sameRowWidgets.length === 0) {
      setTotalHeight(500);
      return () => {};
    }

    const gridElement = document.querySelector('.dashboard-wrapper');

    if (!gridElement) return () => {};

    const computedStyle = window.getComputedStyle(gridElement);

    const rowGap = parseFloat(computedStyle.rowGap || computedStyle.gridRowGap);

    const updateDimensions = () => {
      let total = 0;

      sameRowWidgets.forEach((id) => {
        const element = document.querySelector(`#dashboard-component-${id} > .dashboard-section`);
        if (element) {
          total += element.offsetHeight;
        }
      });

      total -= rowGap / 1.6;

      const rowGapCalculated = rowGap * (sameRowWidgets.length - 1);

      const totalHeightElement = total === 0 ? 0 : total + rowGapCalculated;

      setTotalHeight(totalHeightElement);
    };

    updateDimensions();

    const resizeObserver = new ResizeObserver(updateDimensions);

    sameRowWidgets.forEach((id) => {
      const element = document.querySelector(`#dashboard-component-${id} > .dashboard-section`);
      if (element) resizeObserver.observe(element);
    });

    return () => {
      sameRowWidgets.forEach((id) => {
        const element = document.querySelector(`#dashboard-component-${id} > .dashboard-section`);
        if (element) resizeObserver.unobserve(element);
      });
    };
  }, [sameRowWidgets]);

  useEffect(() => {
    if (!titleRef.current) return;

    const style = window.getComputedStyle(titleRef.current);
    const { height } = titleRef.current.getBoundingClientRect();
    const marginTop = parseFloat(style.marginTop);
    const marginBottom = parseFloat(style.marginBottom);

    setTitleHeight(height + marginTop + marginBottom);
  }, [titleRef]);

  return (
    <div className="dashboard-section">
      <div className="dashboard-section-title" ref={titleRef}>
        <div>
          <h3>{intl.formatMessage({ defaultMessage: 'Notifications' })}</h3>
        </div>
      </div>
      <div className="dashboard-section-content">
        <div
          className="dashboard-section-notifications-wrapper notifications"
          style={{
            height: totalHeight - titleHeight,
          }}
        >
          <div className="dashboard-section-notifications-header">
            <div>
              <Button
                color="link"
                className={classnames({ active: activeTab === 'all' })}
                onClick={() => setActiveTab('all')}
              >
                {intl.formatMessage({ defaultMessage: 'All' })}
              </Button>
              <Button
                color="link"
                className={classnames({ active: activeTab === 'comments' })}
                onClick={() => setActiveTab('comments')}
              >
                {intl.formatMessage({ defaultMessage: 'Comments' })}
              </Button>
            </div>
            <div className="dashboard-section-notifications-header-icon-buttons">
              <Button
                color="link"
                className="mark-all-read-notifications"
                onClick={handleMarkAllAsRead}
                id="dashboard-notifications-mark-all-as-read"
              >
                <i className="fal fa-check-double" />
              </Button>
              <UncontrolledTooltip target="dashboard-notifications-mark-all-as-read">
                {intl.formatMessage({ defaultMessage: 'Mark all as read' })}
              </UncontrolledTooltip>
              <Button
                color="link"
                className="mark-all-read-notifications"
                onClick={handleMarkAllAsRead}
                id="dashboard-notifications-see-all"
                tag={Link}
                to="/notifications"
              >
                <i className="fal fa-inbox-full" />
              </Button>
              <UncontrolledTooltip target="dashboard-notifications-see-all">
                {intl.formatMessage({ defaultMessage: 'See all' })}
              </UncontrolledTooltip>
            </div>
          </div>
          <div
            className="dashboard-section-notifications"
          >
            {filteredNotifications.length > 0 ? (
              <>
                {filteredNotifications.map(notification => (
                  <NotificationsItem
                    key={notification.id}
                    notification={notification}
                    handleMarkAsRead={() => {}}
                    handleMarkAsUnread={() => {}}
                    handleDelete={() => {}}
                  />
                ))}
              </>
            ) : (
              <>
                {(!notificationsLoading && activeTab === 'all') && (
                  <NothingFound
                    text={intl.formatMessage({ defaultMessage: `You don't have any notifications yet` })}
                    icon="far fa-bell"
                  />
                )}
                {(!notificationsLoading && activeTab !== 'all') && (
                  <NothingFound
                    text={intl.formatMessage({ defaultMessage: `Nothing found` })}
                    icon="far fa-bell"
                  />
                )}
              </>
            )}
            {/* eslint-disable react/no-array-index-key */}
            {notificationsLoading && (
              [...Array(14)].map((_, i) => <WidgetSkeletonNotification key={i} />))}
            {/* eslint-enable react/no-array-index-key */}
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  notifications: state.Notifications.notifications,
  notificationsLoading: state.Notifications.loading,
});

const mapDispatchToProps = (dispatch) => ({
  handleMarkAllAsRead: () => {
    dispatch(markAllNotificationsAsRead());
  },
});

DashboardWidgetNotifications.propTypes = {
  notifications: PropTypes.array.isRequired,
  notificationsLoading: PropTypes.bool.isRequired,
  handleMarkAllAsRead: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardWidgetNotifications);
